/**
 * Validation des corrections de feuilles d'heures par le secrétariat
 * (post-validation admin). Fonction pure, testable unitairement.
 *
 * Mirroir des règles métier définies dans `timesheetValidation.ts` :
 *   - RÈGLE 1 : max 12h par jour (additionne toutes les entries de la même work_date)
 *   - RÈGLE 2 : hours_saturday n'a de sens que le samedi (dow === 6)
 *   - RÈGLE 3 : hours_major (heures majorées = férié/dimanche) n'a de sens
 *     qu'en dehors des jours ouvrés (samedi/dimanche acceptés, lundi-vendredi
 *     bloqués car on n'a pas la liste des jours fériés dans ce contexte)
 *
 * Retourne le 1er message d'erreur rencontré, ou null si tout est valide.
 */

/** Sous-ensemble minimal du CorrectionEntry nécessaire à la validation */
export interface CorrectionEntryLike {
  id: string;
  work_date: string; // YYYY-MM-DD
  hours_normal: number;
  hours_saturday: number;
  hours_major: number;
}

/** Edits en attente, indexés par entryId */
export type CorrectionEdits = Record<string, Partial<CorrectionEntryLike>>;

/**
 * Valide un ensemble de corrections contre les règles métier critiques.
 *
 * @param entries Toutes les entries chargées dans la modale de correction
 *                (nécessaires pour calculer le total par jour, pas seulement
 *                celles en cours de modification).
 * @param edits   Edits en attente, indexés par entryId.
 * @returns       Message d'erreur humain (toast.warning) ou null si OK.
 */
export function validateCorrectionEdits(
  entries: CorrectionEntryLike[],
  edits: CorrectionEdits,
): string | null {
  // Merge : applique les edits en attente pour obtenir l'état cible
  const merged = entries.map(e => ({
    ...e,
    ...(edits[e.id] ?? {}),
  }));

  // RÈGLE 1 : max 12h par jour (somme toutes entries de la même work_date,
  // tous types d'heures confondus)
  const byDate: Record<string, number> = {};
  for (const e of merged) {
    const total =
      Number(e.hours_normal || 0) +
      Number(e.hours_saturday || 0) +
      Number(e.hours_major || 0);
    byDate[e.work_date] = (byDate[e.work_date] || 0) + total;
  }
  for (const [date, total] of Object.entries(byDate)) {
    if (total > 12) {
      const label = formatDayLabel(date);
      return `${label} : ${total}h dépasse le maximum de 12h par jour.`;
    }
  }

  // RÈGLES 2-3 : cohérence par type d'heure (sur les entries réellement modifiées
  // — pas de faux positif si une valeur existante non touchée est incohérente)
  for (const [entryId, entryEdits] of Object.entries(edits)) {
    if (!entryEdits || Object.keys(entryEdits).length === 0) continue;
    const e = merged.find(m => m.id === entryId);
    if (!e) continue;

    const dow = dayOfWeek(e.work_date); // 0=dim, 1=lun, ..., 6=sam
    const label = formatDayLabel(e.work_date);

    // hours_saturday ne doit exister que le samedi (dow === 6)
    if ('hours_saturday' in entryEdits && Number(e.hours_saturday) > 0 && dow !== 6) {
      return `${label} : des heures samedi sont renseignées sur un jour qui n'est pas un samedi.`;
    }

    // hours_major (férié/dimanche) interdit du lundi au vendredi
    // (on accepte samedi/dimanche pour couvrir le cas d'un férié tombant un samedi)
    if ('hours_major' in entryEdits && Number(e.hours_major) > 0 && dow >= 1 && dow <= 5) {
      return `${label} : des heures majorées sont renseignées sur un jour ouvré non férié. Si c'est un jour férié, vérifiez la saisie initiale.`;
    }
  }

  return null;
}

/** Formate une date YYYY-MM-DD en "lun. 17 mars" (locale fr-FR) */
function formatDayLabel(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

/** Day of week : 0=dimanche, 1=lundi, ..., 6=samedi */
function dayOfWeek(isoDate: string): number {
  return new Date(isoDate).getDay();
}
